import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Check, UserPlus } from 'lucide-react';
import { BRAND_IDS, type ProvisionSalespersonResponse } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useOpsUsers, useProvisionSalesperson } from '../api/opsUsers.ts';
import { useInviteActivationMintDefaults } from '../api/inviteCodes.ts';
import { CustomInviteCodeField } from './CustomInviteCodeField.tsx';
import { ModelPlanSelect } from './ModelPlanSelect.tsx';
import { useClipboard } from '../hooks/useClipboard.tsx';
import {
  composeCustomInviteCode,
  describeInviteCodeTextError,
  formatInviteCodeFormatError,
} from '../lib/customInviteCode.ts';
import {
  CENTS_PER_YUAN,
  parseGrantYuanToCents,
  parsePositiveInt,
} from '../lib/inviteCodeOverrides.ts';
import { formatCnyCents } from './formatters.ts';
import {
  formatOpsUserPickerLabel,
  opsUserPickerIdentityFromRecord,
} from '../lib/opsUserPickerLabel.ts';
import { OpsUserSheet } from './OpsUserSheet.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, Select } from '../ui/index.ts';

/**
 * Brand the salesperson form starts on.
 *
 * Deliberately not `DEFAULT_BRAND_ID` from `brands.ts` — that constant is the
 * ingress attribution fallback (which brand to stamp on a row whose origin
 * didn't match a host), a different question from "which brand does a new
 * salesperson usually sell for". Salespeople are hired onto Nxtpath, so that
 * is the pre-selection; the picker still offers every `BRAND_IDS` entry.
 */
const PROVISION_DEFAULT_BRAND: string = 'nxtpath';

/**
 * One-click salesperson provisioning.
 *
 * Collapses what used to be four separate operator actions — create the ops
 * user, attach a sales brand, set a login password, mint the invite code —
 * into a single server-side transaction, so a failure halfway through can no
 * longer leave a half-built account behind.
 *
 * The password is generated by platform-api and returned exactly once. There
 * is no endpoint that reads it back, so this panel is the only chance to
 * capture it: it is held in component state (never in the query cache), the
 * copy string is offered up front, and closing asks for confirmation.
 * Recovering from a lost password means a password invite or the
 * salesperson's own self-service change, not a re-read.
 *
 * The three invite-code parameters (#2022 Part A) are here for the same
 * reason they are in the mint dialog: the code this drawer mints is a real
 * code, and one that silently inherited whatever the global settings happened
 * to say at activation time would be the one code nobody had confirmed. They
 * are prefilled from the same `/defaults` read and posted back as an explicit
 * snapshot.
 *
 * Lives in `components/` rather than on a page because the entry point moved
 * to the invite-codes page (#2022): what this drawer produces is an invite
 * code, and the salesperson is the account that owns it. The i18n keys stay
 * under `ops_users.provision.*` — the component's job did not change, only
 * where the operator reaches it.
 */
export function ProvisionSalespersonSheet({
  onClose,
}: {
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const provision = useProvisionSalesperson();
  // Same-name warning source. `GET /v1/ops/ops-users` is admin-only; this
  // sheet is mounted only on the admin path (InviteCodesPage). If it is ever
  // opened to other roles, widen that route too, or the warning silently
  // disappears (a failed query yields no matches, never blocks submit).
  const opsUsers = useOpsUsers();
  const clipboard = useClipboard();
  const defaultsQuery = useInviteActivationMintDefaults();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [brand, setBrand] = useState<string>(PROVISION_DEFAULT_BRAND);
  const [customSuffix, setCustomSuffix] = useState('');
  const [customCodeError, setCustomCodeError] = useState<string | null>(null);
  const [grantYuan, setGrantYuan] = useState('');
  const [expiryDays, setExpiryDays] = useState('');
  const [quota, setQuota] = useState('');
  const [planId, setPlanId] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionSalespersonResponse | null>(null);

  const sameNameUsers = useMemo(() => {
    const typed = displayName.trim().toLowerCase();
    if (typed.length === 0) {
      return [];
    }
    return (opsUsers.data?.users ?? []).filter(
      (user) =>
        user.disabled_at === null && user.display_name.trim().toLowerCase() === typed,
    );
  }, [displayName, opsUsers.data]);

  const defaults = defaultsQuery.data?.defaults ?? null;

  // Prefill once. Re-running after the operator has edited a box would
  // overwrite their number with the recommendation on any refetch.
  useEffect(() => {
    if (prefilled || defaults === null) return;
    setGrantYuan((defaults.grant_amount_cny_cents / CENTS_PER_YUAN).toFixed(2));
    setExpiryDays(String(defaults.grant_expiry_days));
    setQuota(String(defaults.default_daily_quota));
    setPrefilled(true);
  }, [prefilled, defaults]);

  const copyString =
    result === null ? '' : `${result.user.email}/${result.password}/${result.invite_code.code}`;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const name = displayName.trim();
    if (name.length === 0) {
      setError(t('ops_users.provision.error.display_name_required'));
      return;
    }
    // Without the defaults the boxes are empty strings, and sending those as
    // "what the operator confirmed" would mint a code nobody configured.
    if (defaults === null) {
      setError(t('ops_users.provision.error.defaults'));
      return;
    }
    const parsedGrant = parseGrantYuanToCents(grantYuan);
    if (parsedGrant === null) {
      setError(t('ops_users.provision.error.grant'));
      return;
    }
    if (parsedGrant > defaults.max_grant_amount_cny_cents) {
      setError(
        t('ops_users.provision.error.grant_cap', {
          max: formatCnyCents(defaults.max_grant_amount_cny_cents, false),
        }),
      );
      return;
    }
    const parsedExpiry = parsePositiveInt(expiryDays);
    if (parsedExpiry === null) {
      setError(t('ops_users.provision.error.expiry'));
      return;
    }
    const parsedQuota = parsePositiveInt(quota);
    if (parsedQuota === null) {
      setError(t('ops_users.provision.error.quota'));
      return;
    }
    const composed = composeCustomInviteCode(customSuffix, brand);
    if (composed.kind === 'invalid') {
      setCustomCodeError(formatInviteCodeFormatError(t, brand));
      return;
    }
    setCustomCodeError(null);
    const inviteCode = composed.kind === 'ok' ? composed.code : null;
    try {
      const response = await provision.mutateAsync({
        email: email.trim(),
        display_name: name,
        brand,
        grant_amount_cny_cents_override: parsedGrant,
        grant_expiry_days_override: parsedExpiry,
        daily_quota_override: parsedQuota,
        ...(planId === null ? {} : { model_plan_id: planId }),
        ...(inviteCode === null ? {} : { invite_code: inviteCode }),
      });
      setResult(response);
      setCustomSuffix('');
      setCustomCodeError(null);
      toast.success(t('ops_users.toast.provisioned'));
    } catch (err) {
      const specific = describeInviteCodeTextError(err, t, {
        code: inviteCode ?? '',
        brand,
        takenKey: 'ops_users.provision.invite_code_taken',
      });
      const m =
        specific ??
        (err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
      setError(m);
      toast.error(m);
    }
  }

  function handleClose(): void {
    if (result !== null && !window.confirm(t('ops_users.provision.result.close_confirm'))) {
      return;
    }
    setCustomSuffix('');
    setCustomCodeError(null);
    onClose();
  }

  return (
    <OpsUserSheet
      title={t('ops_users.action.provision')}
      titleId="ops-users-provision-sheet-title"
      testId="ops-users-provision-sheet"
      onClose={handleClose}
    >
      {result !== null ? (
        <div
          className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
          data-testid="ops-users-provision-result"
        >
          <p
            className="m-0 rounded-lg border border-warn-line bg-warn-dim px-3 py-2 text-label text-warn"
            role="alert"
            data-testid="ops-users-provision-warning"
          >
            {t('ops_users.provision.result.warning')}
          </p>
          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-3">
            <ResultRow label={t('ops_users.provision.result.name')} value={result.user.display_name} />
            <ResultRow label={t('ops_users.provision.result.email')} value={result.user.email} />
            <ResultRow
              label={t('ops_users.provision.result.password')}
              value={result.password}
              testId="ops-users-provision-password"
            />
            <ResultRow
              label={t('ops_users.provision.result.invite_code')}
              value={result.invite_code.code}
              testId="ops-users-provision-invite-code"
            />
            <ResultRow label={t('ops_users.provision.result.brand')} value={result.sales_profile.brand} />
          </dl>
          <FormField
            label={t('ops_users.provision.result.copy_label')}
            fieldId="ops-users-provision-copy"
          >
            <div className="flex items-center gap-2">
              <Input
                id="ops-users-provision-copy"
                type="text"
                mono
                readOnly
                value={copyString}
                onFocus={(e) => e.currentTarget.select()}
                data-testid="ops-users-provision-copy-value"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void clipboard.copy(copyString);
                }}
                data-testid="ops-users-provision-copy"
              >
                {clipboard.copied
                  ? t('ops_users.provision.result.copied')
                  : t('ops_users.provision.result.copy')}
              </Button>
            </div>
          </FormField>
          {clipboard.error !== null ? (
            <p className="m-0 text-label text-bad" role="alert">
              {t('ops_users.provision.result.copy_failed')}
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              onClick={handleClose}
              data-testid="ops-users-provision-done"
            >
              <Check aria-hidden="true" className="h-4 w-4" />
              {t('ops_users.provision.result.close')}
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="flex min-h-0 flex-1 flex-col"
          data-testid="ops-users-provision-form"
          onSubmit={(e) => void handleSubmit(e)}
        >
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
            <p className="m-0 text-dense text-paper-low">{t('ops_users.provision.intro')}</p>
            <FormField
              label={t('ops_users.provision.display_name')}
              fieldId="ops-users-provision-display-name"
            >
              <Input
                id="ops-users-provision-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                data-testid="ops-users-provision-display-name"
                required
              />
              {sameNameUsers.length > 0 ? (
                <div
                  className="mt-2 flex flex-col gap-1"
                  data-testid="ops-users-provision-duplicate-name-warning"
                >
                  <p className="m-0 text-dense text-warn">
                    {t('ops_users.provision.duplicate_name_warning', { name: displayName.trim() })}
                  </p>
                  <ul className="m-0 pl-4">
                    {sameNameUsers.map((user) => (
                      <li key={user.id}>
                        {formatOpsUserPickerLabel(opsUserPickerIdentityFromRecord(user), t, {
                          withIdPrefix: true,
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </FormField>
            <FormField label={t('ops_users.provision.email')} fieldId="ops-users-provision-email">
              <Input
                id="ops-users-provision-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                data-testid="ops-users-provision-email"
                placeholder="name@example.com"
                required
              />
            </FormField>
            <FormField label={t('ops_users.provision.brand')} fieldId="ops-users-provision-brand">
              <Select
                id="ops-users-provision-brand"
                value={brand}
                onValueChange={(next) => {
                  setBrand(next);
                  setPlanId(null);
                  setCustomSuffix('');
                  setCustomCodeError(null);
                }}
                mono
                options={BRAND_IDS.map((id) => ({ value: id, label: id }))}
                testId="ops-users-provision-brand"
              />
            </FormField>
            <CustomInviteCodeField
              fieldId="ops-users-provision-invite-code"
              testId="ops-users-provision-invite-code"
              label={t('ops_users.provision.invite_code_label')}
              hintKey="ops_users.provision.invite_code_hint"
              brand={brand}
              value={customSuffix}
              onChange={(next) => {
                setCustomSuffix(next);
                setCustomCodeError(null);
              }}
              error={customCodeError}
              disabled={provision.isPending}
            />
            <FormField
              label={t('ops_users.provision.grant_label')}
              fieldId="ops-users-provision-grant"
              hint={t('ops_users.provision.grant_hint')}
            >
              <Input
                id="ops-users-provision-grant"
                type="text"
                value={grantYuan}
                onChange={(e) => setGrantYuan(e.currentTarget.value)}
                inputMode="decimal"
                data-testid="ops-users-provision-grant"
              />
            </FormField>
            <FormField
              label={t('ops_users.provision.expiry_label')}
              fieldId="ops-users-provision-expiry"
              hint={t('ops_users.provision.expiry_hint')}
            >
              <Input
                id="ops-users-provision-expiry"
                type="text"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.currentTarget.value)}
                inputMode="numeric"
                data-testid="ops-users-provision-expiry"
              />
            </FormField>
            <FormField
              label={t('ops_users.provision.quota_label')}
              fieldId="ops-users-provision-quota"
              hint={t('ops_users.provision.quota_hint')}
            >
              <Input
                id="ops-users-provision-quota"
                type="text"
                value={quota}
                onChange={(e) => setQuota(e.currentTarget.value)}
                inputMode="numeric"
                data-testid="ops-users-provision-quota"
              />
            </FormField>
            <ModelPlanSelect
              brand={brand}
              value={planId}
              onChange={setPlanId}
              allowNone
              fieldId="ops-users-provision-plan"
              label={t('ops_users.provision.plan_label')}
              hint={t('ops_users.provision.plan_hint')}
              testId="ops-users-provision-plan"
            />
            {defaultsQuery.isError ? (
              <p
                className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
                role="alert"
                data-testid="ops-users-provision-defaults-error"
              >
                {t('ops_users.provision.error.defaults')}
              </p>
            ) : null}
            {error !== null ? (
              <p
                className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
                role="alert"
                data-testid="ops-users-provision-error"
              >
                {error}
              </p>
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-line-soft px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={provision.isPending}>
              {t('ops_users.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={provision.isPending || brand.length === 0 || !prefilled}
              data-testid="ops-users-provision-submit"
            >
              <UserPlus aria-hidden="true" className="h-4 w-4" />
              {provision.isPending
                ? t('ops_users.action.provisioning')
                : t('ops_users.action.provision')}
            </Button>
          </div>
        </form>
      )}
    </OpsUserSheet>
  );
}

function ResultRow({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <>
      <dt className="m-0 text-label text-paper-low">{label}</dt>
      <dd className="m-0 min-w-0 break-all font-mono text-dense text-paper" data-testid={testId}>
        {value}
      </dd>
    </>
  );
}
